import { makeAutoObservable, runInAction } from 'mobx'
import { notification } from 'antd'
import { openApi } from '../../helpers/request'
import i from '../../translations/i'
import superController, { SuperProject } from './super.controller'

// Включение функций платформы по проектам и пользователям. Функции задаёт сервер (App\Domain\Features\Feature),
// здесь только их список и решения: «выключено», «выбранным пользователям», «всем в проекте»

export type RolloutMode = 'off' | 'users' | 'all'
// Фильтр списка проектов: любые, где функция включена (выбранным или всем), где выключена
export type RolloutFilter = 'any' | 'enabled' | 'off'

export interface Rollout {
    mode: RolloutMode
    user_ids: number[]
    updated_at: string | null
}

export interface SuperFeature {
    key: string
    title: string
    description: string
    // Только проекты, где что-то задавали
    rollouts: (Rollout & { setting_id: number })[]
}

export interface FeatureUser {
    id: number
    name: string
    phone: string | null
    email: string | null
    registered: boolean
}

const OFF: Rollout = { mode: 'off', user_ids: [], updated_at: null }

// Подпись пользователя в списке выбора: «Анна Иванова · +79001112233 · #5»
export const userLabel = (user: FeatureUser | undefined, id: number) => {
    if (!user) return `#${id}`

    const parts = [user.name, user.phone, user.email].filter(Boolean)
    if (!parts.length) parts.push(i.t('superAdmin:features.guest'))

    return `${parts.join(' · ')} · #${id}`
}

class featuresController {
    features: SuperFeature[] = []
    loading = false
    selectedKey: string | null = null

    search = ''
    filter: RolloutFilter = 'any'

    // Окно настройки одного проекта
    editing: SuperProject | null = null
    draftMode: RolloutMode = 'off'
    draftUserIds: number[] = []
    saving = false
    // Подписи пользователей: id -> пользователь, копятся по мере поиска
    users: Record<number, FeatureUser> = {}
    usersFound: number[] = []
    usersSearching = false
    private searchTimer: ReturnType<typeof setTimeout> | null = null
    private searchSeq = 0

    constructor() {
        makeAutoObservable(this)
    }

    load() {
        this.loading = true
        superController.getProjects()

        openApi('get', '/protected/features')
            .then(result => runInAction(() => {
                this.features = result.data?.features ?? []
                if (!this.selectedKey || !this.features.some(feature => feature.key === this.selectedKey)) {
                    this.selectedKey = this.features[0]?.key ?? null
                }
            }))
            .catch(err => console.log(err))
            .finally(() => runInAction(() => this.loading = false))
    }

    get selected(): SuperFeature | null {
        return this.features.find(feature => feature.key === this.selectedKey) ?? null
    }

    select(key: string) {
        this.selectedKey = key
    }

    rolloutOf(projectId: number): Rollout {
        return this.selected?.rollouts.find(rollout => rollout.setting_id === projectId) ?? OFF
    }

    get enabledCount(): number {
        return superController.projects.filter(project => this.rolloutOf(project.id).mode !== 'off').length
    }

    // Сколько проектов под каждым фильтром: для счётчиков на чипах
    get counts(): Record<RolloutFilter, number> {
        const total = superController.projects.length

        return { any: total, enabled: this.enabledCount, off: total - this.enabledCount }
    }

    get filteredProjects(): SuperProject[] {
        const enabled = (project: SuperProject) => this.rolloutOf(project.id).mode !== 'off'

        return superController.filteredProjects
            .filter(project => this.filter === 'any' || enabled(project) === (this.filter === 'enabled'))
            // Сначала проекты, где функция включена: ради них страницу и открывают
            .slice()
            .sort((a, b) => Number(enabled(b)) - Number(enabled(a)))
    }

    edit(project: SuperProject) {
        const rollout = this.rolloutOf(project.id)

        this.editing = project
        this.draftMode = rollout.mode
        this.draftUserIds = [...rollout.user_ids]
        this.usersFound = []

        if (rollout.user_ids.length) this.fetchUsers({ ids: rollout.user_ids.join(',') }, false)
    }

    close() {
        this.editing = null
    }

    setMode(mode: RolloutMode) {
        this.draftMode = mode
    }

    setUserIds(ids: number[]) {
        this.draftUserIds = ids
    }

    // Строка поиска набирается по буквам: запрос уходит, когда человек остановился
    searchUsers(text: string) {
        if (this.searchTimer) clearTimeout(this.searchTimer)

        const search = text.trim()
        if (!search) {
            this.usersFound = []
            return
        }

        this.searchTimer = setTimeout(() => this.fetchUsers({ search }, true), 300)
    }

    private fetchUsers(params: { search?: string, ids?: string }, asResult: boolean) {
        if (!this.editing) return

        const seq = ++this.searchSeq
        const query = new URLSearchParams(params as Record<string, string>).toString()
        if (asResult) this.usersSearching = true

        openApi('get', `/protected/features/projects/${this.editing.id}/users?${query}`, {}, undefined, true)
            .then(result => runInAction(() => {
                const found: FeatureUser[] = result.data ?? []
                found.forEach(user => this.users[user.id] = user)

                // Ответ на устаревший запрос не затирает свежий
                if (asResult && seq === this.searchSeq) this.usersFound = found.map(user => user.id)
            }))
            .catch(err => notification.error({ message: err?.response?.data?.data?.message ?? i.t('def:errors.error') }))
            .finally(() => runInAction(() => {
                if (asResult && seq === this.searchSeq) this.usersSearching = false
            }))
    }

    get canSave(): boolean {
        return !this.saving && (this.draftMode !== 'users' || this.draftUserIds.length > 0)
    }

    save() {
        const project = this.editing
        const feature = this.selected
        if (!project || !feature || !this.canSave) return

        this.saving = true

        openApi('post', `/protected/features/${feature.key}/projects/${project.id}`, { mode: this.draftMode, user_ids: this.draftUserIds })
            .then(result => runInAction(() => {
                const saved: Rollout = result.data
                const rest = feature.rollouts.filter(rollout => rollout.setting_id !== project.id)

                feature.rollouts = [...rest, { setting_id: project.id, mode: saved.mode, user_ids: saved.user_ids, updated_at: saved.updated_at }]
                this.editing = null

                notification.success({ message: i.t('def:success.complete') })
            }))
            .catch(err => notification.error({ message: err?.response?.data?.data?.message ?? i.t('def:errors.error') }))
            .finally(() => runInAction(() => this.saving = false))
    }
}

export default new featuresController()
